import React from 'react';

import { IconArrowHead, NamedLink, ResponsiveImage } from '../../../components';
import { useConfiguration } from '../../../context/configurationContext';
import { createSlug } from '../../../util/urlHelpers';

import css from './ISOResultCard.module.css';

const ISO_LISTING_TYPE = 'iso-book';

const ISOResultCard = ({ listing }) => {
  const config = useConfiguration();
  const attributes = listing?.attributes || {};
  const publicData = attributes.publicData || {};
  const title = attributes.title || 'ISO request';
  const requester = listing?.author?.attributes?.profile?.displayName;
  const image = listing?.images?.[0];
  const { variantPrefix = 'listing-card' } = config?.layout?.listingImage || {};
  const variants = image
    ? Object.keys(image.attributes?.variants || {}).filter(name => name.startsWith(variantPrefix))
    : [];
  const slug = createSlug(title);
  const isISO = publicData.listingType === ISO_LISTING_TYPE;
  const edition = publicData.edition || publicData.editionsource;
  const budget = publicData.budget || publicData.maxBudget;
  const condition = publicData.condition || publicData.conditionPreference;
  const postedDate = attributes.createdAt
    ? new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }).format(
        new Date(attributes.createdAt)
      )
    : null;
  const fieldLabel = (key, value) => {
    const field = config?.listing?.listingFields?.find(item => item.key === key);
    return field?.enumOptions?.find(option => option.option === value)?.label || value;
  };

  if (!isISO) {
    return null;
  }

  return (
    <article className={css.card}>
      <div className={css.imageFrame}>
        {image ? (
          <NamedLink className={css.imageLink} name="ListingPage" params={{ id: listing.id.uuid, slug }}>
            <ResponsiveImage
              rootClassName={css.image}
              alt={title}
              image={image}
              variants={variants}
              sizes="(max-width: 549px) 28vw, (max-width: 1023px) 18vw, 120px"
            />
          </NamedLink>
        ) : (
          <div className={css.imagePlaceholder} aria-hidden="true" />
        )}
      </div>
      <div className={css.cardContent}>
        <div className={css.cardTopline}>
          <span className={css.pill}>IN SEARCH OF</span>
          {postedDate ? <span className={css.postedDate}>{postedDate}</span> : null}
        </div>
        <NamedLink className={css.cardTitle} name="ListingPage" params={{ id: listing.id.uuid, slug }}>
          {title}
        </NamedLink>
        {publicData.author ? <div className={css.detail}>{publicData.author}</div> : null}
        {edition ? (
          <div className={css.detail}>
            <span className={css.detailLabel}>Edition wanted</span>
            {fieldLabel('editionsource', edition)}
          </div>
        ) : null}
        {budget ? (
          <div className={css.detail}>
            <span className={css.detailLabel}>Budget</span>
            {budget}
          </div>
        ) : null}
        {condition ? (
          <div className={css.detail}>
            <span className={css.detailLabel}>Condition</span>
            {fieldLabel('condition', condition)}
          </div>
        ) : null}
        {publicData.signedpreference ? (
          <span className={css.preference}>
            {fieldLabel('signedpreference', publicData.signedpreference)}
          </span>
        ) : null}
        {publicData.notes ? <div className={css.note}>{publicData.notes}</div> : null}
        {requester ? <div className={css.requester}>Requested by {requester}</div> : null}
        <NamedLink className={css.cardLink} name="NewListingPage" to={{ search: '?listingType=book-listing' }}>
          I Have This <IconArrowHead direction="right" size="small" />
        </NamedLink>
      </div>
    </article>
  );
};

export default ISOResultCard;
